import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import StandingsWidget from '../../components/stats/StandingsWidget';
import { getRankingCompetitionMeta, getWidgetConfiguration } from '../../../api';
import { resolveWidgetDisplayTitle } from '../../utils/widgetDisplayTitle';

const normalizeDayValue = (value) => {
  if (value == null || value === '') return null;

  const stringValue = String(value).trim();
  if (!stringValue) return null;

  const parsed = Number(stringValue);
  return Number.isFinite(parsed) ? parsed : stringValue;
};

const isNumericDayValue = (value) =>
  typeof value === 'number' && Number.isFinite(value);

const MAX_SELECTED_COMPETITIONS = 5;

const clampCompetitionIds = (value = '') =>
  String(value || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
    .slice(0, MAX_SELECTED_COMPETITIONS);

const VALID_STAT_TYPES = ['general', 'home', 'away'];

const normalizeStatType = (value) =>
  VALID_STAT_TYPES.includes(value) ? value : 'general';

export default function StandingsPage({
  competitionIds: competitionIdsProp = null,
  day: dayProp = null,
  widgetConfigurationId: widgetConfigurationIdProp,
  embedded = false,
  title: titleProp,
  showCompetitionInfos: showCompetitionInfosProp,
  onNavigate,
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const hasCompetitionIdsProp = Array.isArray(competitionIdsProp);
  const competitionIdsParam = hasCompetitionIdsProp
    ? competitionIdsProp.join(',')
    : searchParams.get('competitionIds') || '';

  const hasDayProp = dayProp != null && dayProp !== '';
  const dayParam = hasDayProp ? String(dayProp) : searchParams.get('day');

  const widgetConfigurationId =
    widgetConfigurationIdProp ??
    (embedded ? '' : searchParams.get('widgetConfigurationId') || '');

  const shouldSyncSearchParams =
    !embedded && !hasCompetitionIdsProp && !hasDayProp;

  const [widgetConfig, setWidgetConfig] = useState(null);
  const [localStatType, setLocalStatType] = useState('general');

  const groupKeyParam = embedded ? '' : searchParams.get('groupKey') || '';
  const [localGroupKey, setLocalGroupKey] = useState('');
  const resolvedGroupKey = embedded ? localGroupKey : groupKeyParam;

  const competitionIds = useMemo(
    () => clampCompetitionIds(competitionIdsParam),
    [competitionIdsParam]
  );

  const isSingleCompetition = competitionIds.length === 1;
  const firstCompetitionId = competitionIds[0];

  const [discipline, setDiscipline] = useState(null);
  const [title, setTitle] = useState('Classement');
  const [competitionInfos, setCompetitionInfos] = useState([]);
  const [resolvedDay, setResolvedDay] = useState(null);

  const statTypeParam = embedded
    ? localStatType
    : searchParams.get('statType') || '';
  const normalizedStatType = normalizeStatType(statTypeParam);

  const handleStatTypeChange = (nextStatType) => {
    const normalizedNextStatType = normalizeStatType(nextStatType);

    if (embedded) {
      setLocalStatType(normalizedNextStatType);
      return;
    }

    const nextParams = new URLSearchParams(searchParams);

    if (!nextStatType || normalizedNextStatType === 'general') {
      nextParams.delete('statType');
    } else {
      nextParams.set('statType', normalizedNextStatType);
    }

    if (widgetConfigurationId) {
      nextParams.set('widgetConfigurationId', widgetConfigurationId);
    }

    setSearchParams(nextParams, { replace: true });
  };

  const handleGroupChange = (nextGroupKey) => {
    const normalized =
      nextGroupKey != null && String(nextGroupKey).trim() !== ''
        ? String(nextGroupKey).trim()
        : '';

    if (embedded) {
      setLocalGroupKey(normalized);
      return;
    }

    const nextParams = new URLSearchParams(searchParams);

    if (normalized) {
      nextParams.set('groupKey', normalized);
    } else {
      nextParams.delete('groupKey');
    }

    if (widgetConfigurationId) {
      nextParams.set('widgetConfigurationId', widgetConfigurationId);
    }

    setSearchParams(nextParams, { replace: true });
  };


  const handleWidgetDayChange = (nextDay) => {
  const normalized = normalizeDayValue(nextDay);

  if (embedded || hasDayProp) {
    setResolvedDay(normalized);
    return;
  }

  const nextParams = new URLSearchParams(searchParams);

  if (normalized == null) {
    nextParams.delete('day');
  } else {
    nextParams.set('day', String(normalized));
  }

  if (widgetConfigurationId) {
    nextParams.set('widgetConfigurationId', widgetConfigurationId);
  }

  setSearchParams(nextParams, { replace: true });
};

  useEffect(() => {
    let ignore = false;

    const loadMeta = async () => {
      if (!firstCompetitionId) {
        if (!ignore) {
          setDiscipline(null);
          setResolvedDay(null);
          setTitle('Classement');
          setCompetitionInfos([]);
        }
        return;
      }

      try {
        const metas = await Promise.all(
          competitionIds.map(async (competitionId) => {
            const { data } = await getRankingCompetitionMeta(competitionId);
            return data;
          })
        );

        if (ignore) return;

        const firstMeta = metas[0] || {};
        const metaDiscipline = firstMeta?.discipline || null;
        const metaNbDays = Number(firstMeta?.nbDays);
        const metaDefaultDayRaw = firstMeta?.defaultDay;
        const metaDefaultDay = normalizeDayValue(metaDefaultDayRaw);

        setDiscipline(metaDiscipline);
        setTitle(isSingleCompetition ? 'Classement' : 'Classement combiné');
        setCompetitionInfos(
          metas
            .map((meta, index) => ({
              id: competitionIds[index],
              name: meta?.name || '',
              season: meta?.season || '',
              nbDays: meta?.nbDays ?? null,
              defaultDay: meta?.defaultDay ?? null,
              rounds: Array.isArray(meta?.rounds) ? meta.rounds : [],
              rankingMode: meta?.rankingMode || 'classic',
              groups: Array.isArray(meta?.groups) ? meta.groups : [],
            }))
            .filter((item) => item.name)
        );

        const parsedDayFromUrl = normalizeDayValue(dayParam);

        if (!isSingleCompetition) {
          setResolvedDay(null);

          if (shouldSyncSearchParams && dayParam) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete('day');
            setSearchParams(nextParams, { replace: true });
          }

          return;
        }

        if (parsedDayFromUrl != null) {
          let boundedDay = parsedDayFromUrl;

          if (
            isNumericDayValue(boundedDay) &&
            Number.isFinite(metaNbDays) &&
            metaNbDays > 0 &&
            boundedDay > metaNbDays
          ) {
            boundedDay = metaNbDays;
          }

          setResolvedDay(boundedDay);

          if (shouldSyncSearchParams && String(boundedDay) !== String(parsedDayFromUrl)) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('day', String(boundedDay));
            setSearchParams(nextParams, { replace: true });
          }

          return;
        }

        if (metaDefaultDay != null) {
          setResolvedDay(metaDefaultDay);

          if (shouldSyncSearchParams) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('day', String(metaDefaultDay));
            setSearchParams(nextParams, { replace: true });
          }
        } else if (Number.isFinite(metaNbDays) && metaNbDays > 0) {
          setResolvedDay(metaNbDays);

          if (shouldSyncSearchParams) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('day', String(metaNbDays));
            setSearchParams(nextParams, { replace: true });
          }
        } else {
          setResolvedDay(null);
        }
      } catch (error) {
        if (!ignore) {
          setDiscipline(null);
          setResolvedDay(isSingleCompetition ? normalizeDayValue(dayParam) : null);
          setTitle(isSingleCompetition ? 'Classement' : 'Classement combiné');
          setCompetitionInfos([]);
        }
      }
    };

    loadMeta();

    return () => {
      ignore = true;
    };
  }, [
    firstCompetitionId,
    competitionIds,
    dayParam,
    searchParams,
    setSearchParams,
    isSingleCompetition,
    shouldSyncSearchParams,
  ]);

  useEffect(() => {
    let ignore = false;

    const loadWidgetConfig = async () => {
      if (!widgetConfigurationId) {
        if (!ignore) setWidgetConfig(null);
        return;
      }

      try {
        const { data } = await getWidgetConfiguration(widgetConfigurationId);
        if (!ignore) setWidgetConfig(data || null);
      } catch {
        if (!ignore) setWidgetConfig(null);
      }
    };

    loadWidgetConfig();

    return () => {
      ignore = true;
    };
  }, [widgetConfigurationId]);

  const resolvedTitle = useMemo(() => {
    if (titleProp != null) return titleProp;

    return resolveWidgetDisplayTitle({
      widgetConfig,
      competitionInfos,
      fallbackTitle: title,
    });
  }, [widgetConfig, competitionInfos, title, titleProp]);

  const shouldShowWidgetHeader =
    showCompetitionInfosProp ?? Boolean(resolvedTitle);

  if (!discipline || competitionIds.length === 0) return null;

  return (
    <StandingsWidget
      discipline={discipline}
      competitionIds={competitionIds}
      competitionInfos={competitionInfos}
      day={isSingleCompetition ? resolvedDay : null}
      groupKey={resolvedGroupKey}
      onGroupChange={handleGroupChange}
      onDayChange={handleWidgetDayChange}
      widgetConfigurationId={widgetConfigurationId || null}
      title={resolvedTitle}
      showCompetitionInfos={shouldShowWidgetHeader}
      statType={normalizedStatType}
      onStatTypeChange={handleStatTypeChange}
      onNavigate={(url) => (onNavigate ? onNavigate(url) : navigate(url))}
    />
  );
}
